/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Injector, THROW_IF_NOT_FOUND} from './injector';
import {Self, SkipSelf} from './metadata';
import {Provider} from './provider';
import {cyclicDependencyError, instantiationError, noProviderError, outOfBoundsError} from './reflective_errors';
import {ReflectiveKey} from './reflective_key';
import {ReflectiveDependency, ResolvedReflectiveFactory, ResolvedReflectiveProvider, resolveReflectiveProviders} from './reflective_provider';

// Threshold for the dynamic version
const /** @type {?} */ UNDEFINED = new Object();
/**
 * A ReflectiveDependency injection container used for instantiating objects and resolving
 * dependencies.
 * 
 * An `Injector` is a replacement for a `new` operator, which can automatically resolve the
 * constructor dependencies.
 * 
 * In typical use, application code asks for the dependencies in the constructor and they are
 * resolved by the `Injector`.
 * 
 * ### Example ([live demo](http://plnkr.co/edit/jzjec0?p=preview))
 * 
 * The following example creates an `Injector` configured to create `Engine` and `Car`.
 * 
 * ```typescript
 * \@Injectable() 
 * class Engine {
 * }
 * 
 * \@Injectable() 
 * class Car {
 *   constructor(public engine:Engine) {}
 * }
 * 
 * var injector = ReflectiveInjector.resolveAndCreate([Car, Engine]);
 * var car = injector.get(Car);
 * expect(car instanceof Car).toBe(true);
 * expect(car.engine instanceof Engine).toBe(true);
 * ```
 * 
 * Notice, we don't use the `new` operator because we explicitly want to have the `Injector`
 * resolve all of the object's dependencies automatically.
 * 
 * \@stable
 * @abstract
 */
export abstract class ReflectiveInjector implements Injector {
/**
 * Turns an array of provider definitions into an array of resolved providers.
 * 
 * A resolution is a process of flattening multiple nested arrays and converting individual
 * providers into an array of {\@link ResolvedReflectiveProvider}s.
 * 
 * ### Example ([live demo](http://plnkr.co/edit/AiXTHi?p=preview))
 * 
 * ```typescript
 * \@Injectable() 
 * class Engine {
 * }
 * 
 * \@Injectable() 
 * class Car {
 *   constructor(public engine:Engine) {}
 * }
 * 
 * var providers = ReflectiveInjector.resolve([Car, [[Engine]]]);
 * 
 * expect(providers.length).toEqual(2);
 * 
 * expect(providers[0] instanceof ResolvedReflectiveProvider).toBe(true);
 * expect(providers[0].key.displayName).toBe("Car");
 * expect(providers[0].dependencies.length).toEqual(1);
 * expect(providers[0].factory).toBeDefined();
 * 
 * expect(providers[1].key.displayName).toBe("Engine");
 * });
 * ```
 * 
 * See {\@link ReflectiveInjector#fromResolvedProviders} for more info.
 * @param {?} providers
 * @return {?}
 */
static resolve(providers: Provider[]): ResolvedReflectiveProvider[] {
    return resolveReflectiveProviders(providers);
  }
/**
 * Resolves an array of providers and creates an injector from those providers.
 * 
 * The passed-in providers can be an array of `Type`, {\@link Provider},
 * or a recursive array of more providers.
 * 
 * ### Example ([live demo](http://plnkr.co/edit/ePOccA?p=preview))
 * 
 * ```typescript
 * \@Injectable() 
 * class Engine {
 * }
 * 
 * \@Injectable() 
 * class Car {
 *   constructor(public engine:Engine) {}
 * }
 * 
 * var injector = ReflectiveInjector.resolveAndCreate([Car, Engine]);
 * expect(injector.get(Car) instanceof Car).toBe(true);
 * ```
 * 
 * This function is slower than the corresponding `fromResolvedProviders`
 * because it needs to resolve the passed-in providers first.
 * See {\@link Injector#resolve} and {\@link Injector#fromResolvedProviders}.
 * @param {?} providers
 * @param {?=} parent
 * @return {?}
 */
static resolveAndCreate(providers: Provider[], parent?: Injector): ReflectiveInjector {
    const /** @type {?} */ ResolvedReflectiveProviders = ReflectiveInjector.resolve(providers);
    return ReflectiveInjector.fromResolvedProviders(ResolvedReflectiveProviders, parent);
  }
/**
 * Creates an injector from previously resolved providers.
 * 
 * This API is the recommended way to construct injectors in performance-sensitive parts.
 * 
 * ### Example ([live demo](http://plnkr.co/edit/KrSMci?p=preview))
 * 
 * ```typescript
 * \@Injectable() 
 * class Engine {
 * }
 * 
 * \@Injectable() 
 * class Car {
 *   constructor(public engine:Engine) {}
 * }
 * 
 * var providers = ReflectiveInjector.resolve([Car, Engine]);
 * var injector = ReflectiveInjector.fromResolvedProviders(providers);
 * expect(injector.get(Car) instanceof Car).toBe(true);
 * ```
 * \@experimental
 * @param {?} providers
 * @param {?=} parent
 * @return {?}
 */
static fromResolvedProviders(providers: ResolvedReflectiveProvider[], parent?: Injector):
      ReflectiveInjector {
    return new ReflectiveInjector_(providers, parent);
  }
/**
 * Parent of this injector.
 * 
 * <!-- TODO: Add a link to the section of the user guide talking about hierarchical injection.
 * -->
 * 
 * ### Example ([live demo](http://plnkr.co/edit/eosMGo?p=preview))
 * 
 * ```typescript
 * var parent = ReflectiveInjector.resolveAndCreate([]);
 * var child = parent.resolveAndCreateChild([]);
 * expect(child.parent).toBe(parent);
 * ```
 * @abstract
 * @return {?}
 */
parent() {}
/**
 * Resolves an array of providers and creates a child injector from those providers.
 * 
 * <!-- TODO: Add a link to the section of the user guide talking about hierarchical injection.
 * -->
 * 
 * The passed-in providers can be an array of `Type`, {\@link Provider},
 * or a recursive array of more providers.
 * 
 * ### Example ([live demo](http://plnkr.co/edit/opB3T4?p=preview))
 * 
 * ```typescript
 * class ParentProvider {}
 * class ChildProvider {}
 * 
 * var parent = ReflectiveInjector.resolveAndCreate([ParentProvider]);
 * var child = parent.resolveAndCreateChild([ChildProvider]);
 * 
 * expect(child.get(ParentProvider) instanceof ParentProvider).toBe(true);
 * expect(child.get(ChildProvider) instanceof ChildProvider).toBe(true);
 * expect(child.get(ParentProvider)).toBe(parent.get(ParentProvider));
 * ```
 * 
 * This function is slower than the corresponding `createChildFromResolved`
 * because it needs to resolve the passed-in providers first.
 * See {\@link Injector#resolve} and {\@link Injector#createChildFromResolved}.
 * @abstract
 * @param {?} providers
 * @return {?}
 */
resolveAndCreateChild(providers: Provider[]) {}
/**
 * Creates a child injector from previously resolved providers.
 * 
 * <!-- TODO: Add a link to the section of the user guide talking about hierarchical injection.
 * -->
 * 
 * This API is the recommended way to construct injectors in performance-sensitive parts.
 * 
 * ### Example ([live demo](http://plnkr.co/edit/VhyfjN?p=preview))
 * 
 * ```typescript
 * class ParentProvider {}
 * class ChildProvider {}
 * 
 * var parentProviders = ReflectiveInjector.resolve([ParentProvider]);
 * var childProviders = ReflectiveInjector.resolve([ChildProvider]);
 * 
 * var parent = ReflectiveInjector.fromResolvedProviders(parentProviders);
 * var child = parent.createChildFromResolved(childProviders);
 * 
 * expect(child.get(ParentProvider) instanceof ParentProvider).toBe(true);
 * expect(child.get(ChildProvider) instanceof ChildProvider).toBe(true);
 * expect(child.get(ParentProvider)).toBe(parent.get(ParentProvider));
 * ```
 * @abstract
 * @param {?} providers
 * @return {?}
 */
createChildFromResolved(providers: ResolvedReflectiveProvider[]) {}
/**
 * Resolves a provider and instantiates an object in the context of the injector.
 * 
 * The created object does not get cached by the injector.
 * 
 * ### Example ([live demo](http://plnkr.co/edit/yvVXoB?p=preview))
 * 
 * ```typescript
 * \@Injectable() 
 * class Engine {
 * }
 * 
 * \@Injectable() 
 * class Car {
 *   constructor(public engine:Engine) {}
 * }
 * 
 * var injector = ReflectiveInjector.resolveAndCreate([Engine]);
 * 
 * var car = injector.resolveAndInstantiate(Car);
 * expect(car.engine).toBe(injector.get(Engine));
 * expect(car).not.toBe(injector.resolveAndInstantiate(Car));
 * ```
 * @abstract
 * @param {?} provider
 * @return {?}
 */
resolveAndInstantiate(provider: Provider) {}
/**
 * Instantiates an object using a resolved provider in the context of the injector.
 * 
 * The created object does not get cached by the injector.
 * 
 * ### Example ([live demo](http://plnkr.co/edit/ptCImQ?p=preview))
 * 
 * ```typescript
 * \@Injectable() 
 * class Engine {
 * }
 * 
 * \@Injectable() 
 * class Car {
 *   constructor(public engine:Engine) {}
 * }
 * 
 * var injector = ReflectiveInjector.resolveAndCreate([Engine]);
 * var carProvider = ReflectiveInjector.resolve([Car])[0];
 * var car = injector.instantiateResolved(carProvider);
 * expect(car.engine).toBe(injector.get(Engine));
 * expect(car).not.toBe(injector.instantiateResolved(carProvider));
 * ```
 * @abstract
 * @param {?} provider
 * @return {?}
 */
instantiateResolved(provider: ResolvedReflectiveProvider) {}
/**
 * @abstract
 * @param {?} token
 * @param {?=} notFoundValue
 * @return {?}
 */
get(token: any, notFoundValue?: any) {}
}
export class ReflectiveInjector_ implements ReflectiveInjector {
/**
 * \@internal
 */
_constructionCounter: number = 0;
/**
 * \@internal
 */
public _providers: ResolvedReflectiveProvider[];
/**
 * \@internal
 */
public _parent: Injector|null;

  keyIds: number[];
  objs: any[];
/**
 * Private
 * @param {?} _providers
 * @param {?=} _parent
 */
constructor(_providers: ResolvedReflectiveProvider[], _parent?: Injector) {
    this._providers = _providers;
    this._parent = _parent || null;

    const len = _providers.length;

    this.keyIds = new Array(len);
    this.objs = new Array(len);

    for (let i = 0; i < len; i++) {
      this.keyIds[i] = _providers[i].key.id;
      this.objs[i] = UNDEFINED;
    }
  }
/**
 * @param {?} token
 * @param {?=} notFoundValue
 * @return {?}
 */
get(token: any, notFoundValue: any = THROW_IF_NOT_FOUND): any {
    return this._getByKey(ReflectiveKey.get(token), null, notFoundValue);
  }
/**
 * @return {?}
 */
get parent(): Injector|null { return this._parent; }
/**
 * @param {?} providers
 * @return {?}
 */
resolveAndCreateChild(providers: Provider[]): ReflectiveInjector {
    const /** @type {?} */ ResolvedReflectiveProviders = ReflectiveInjector.resolve(providers);
    return this.createChildFromResolved(ResolvedReflectiveProviders);
  }
/**
 * @param {?} providers
 * @return {?}
 */
createChildFromResolved(providers: ResolvedReflectiveProvider[]): ReflectiveInjector {
    const /** @type {?} */ inj = new ReflectiveInjector_(providers);
    inj._parent = this;
    return inj;
  }
/**
 * @param {?} provider
 * @return {?}
 */
resolveAndInstantiate(provider: Provider): any {
    return this.instantiateResolved(ReflectiveInjector.resolve([provider])[0]);
  }
/**
 * @param {?} provider
 * @return {?}
 */
instantiateResolved(provider: ResolvedReflectiveProvider): any {
    return this._instantiateProvider(provider);
  }
/**
 * @param {?} index
 * @return {?}
 */
getProviderAtIndex(index: number): ResolvedReflectiveProvider {
    if (index < 0 || index >= this._providers.length) {
      throw outOfBoundsError(index);
    }
    return this._providers[index];
  }
/**
 * \@internal
 * @param {?} provider
 * @return {?}
 */
_new(provider: ResolvedReflectiveProvider): any {
    if (this._constructionCounter++ > this._getMaxNumberOfObjects()) {
      throw cyclicDependencyError(this, provider.key);
    }
    return this._instantiateProvider(provider);
  }
/**
 * @return {?}
 */
private _getMaxNumberOfObjects(): number { return this.objs.length; }
/**
 * @param {?} provider
 * @return {?}
 */
private _instantiateProvider(provider: ResolvedReflectiveProvider): any {
    if (provider.multiProvider) {
      const /** @type {?} */ res = new Array(provider.resolvedFactories.length);
      for (let /** @type {?} */ i = 0; i < provider.resolvedFactories.length; ++i) {
        res[i] = this._instantiate(provider, provider.resolvedFactories[i]);
      }
      return res;
    } else {
      return this._instantiate(provider, provider.resolvedFactories[0]);
    }
  }
/**
 * @param {?} provider
 * @param {?} ResolvedReflectiveFactory
 * @return {?}
 */
private _instantiate(
      provider: ResolvedReflectiveProvider,
      ResolvedReflectiveFactory: ResolvedReflectiveFactory): any {
    const /** @type {?} */ factory = ResolvedReflectiveFactory.factory;

    let /** @type {?} */ deps: any[];
    try {
      deps =
          ResolvedReflectiveFactory.dependencies.map(dep => this._getByReflectiveDependency(dep));
    } catch ( /** @type {?} */e) {
      if (e.addKey) {
        e.addKey(this, provider.key);
      }
      throw e;
    }

    let /** @type {?} */ obj: any;
    try {
      obj = factory(...deps);
    } catch ( /** @type {?} */e) {
      throw instantiationError(this, e, e.stack, provider.key);
    }

    return obj;
  }
/**
 * @param {?} dep
 * @return {?}
 */
private _getByReflectiveDependency(dep: ReflectiveDependency): any {
    return this._getByKey(dep.key, dep.visibility, dep.optional ? null : THROW_IF_NOT_FOUND);
  }
/**
 * @param {?} key
 * @param {?} visibility
 * @param {?} notFoundValue
 * @return {?}
 */
private _getByKey(key: ReflectiveKey, visibility: Self|SkipSelf|null, notFoundValue: any): any {
    if (key === INJECTOR_KEY) {
      return this;
    }

    if (visibility instanceof Self) {
      return this._getByKeySelf(key, notFoundValue);

    } else {
      return this._getByKeyDefault(key, notFoundValue, visibility);
    }
  }
/**
 * @param {?} keyId
 * @return {?}
 */
private _getObjByKeyId(keyId: number): any {
    for (let /** @type {?} */ i = 0; i < this.keyIds.length; i++) {
      if (this.keyIds[i] === keyId) {
        if (this.objs[i] === UNDEFINED) {
          this.objs[i] = this._new(this._providers[i]);
        }

        return this.objs[i];
      }
    }

    return UNDEFINED;
  }
/**
 * \@internal
 * @param {?} key
 * @param {?} notFoundValue
 * @return {?}
 */
_throwOrNull(key: ReflectiveKey, notFoundValue: any): any {
    if (notFoundValue !== THROW_IF_NOT_FOUND) {
      return notFoundValue;
    } else {
      throw noProviderError(this, key);
    }
  }
/**
 * \@internal
 * @param {?} key
 * @param {?} notFoundValue
 * @return {?}
 */
_getByKeySelf(key: ReflectiveKey, notFoundValue: any): any {
    const /** @type {?} */ obj = this._getObjByKeyId(key.id);
    return (obj !== UNDEFINED) ? obj : this._throwOrNull(key, notFoundValue);
  }
/**
 * \@internal
 * @param {?} key
 * @param {?} notFoundValue
 * @param {?} visibility
 * @return {?}
 */
_getByKeyDefault(key: ReflectiveKey, notFoundValue: any, visibility: Self|SkipSelf|null): any {
    let /** @type {?} */ inj: Injector|null;

    if (visibility instanceof SkipSelf) {
      inj = this._parent;
    } else {
      inj = this;
    }

    while (inj instanceof ReflectiveInjector_) {
      const /** @type {?} */ inj_ = /** @type {?} */(( <ReflectiveInjector_>inj));
      const /** @type {?} */ obj = inj_._getObjByKeyId(key.id);
      if (obj !== UNDEFINED) return obj;
      inj = inj_._parent;
    }
    if (inj !== null) {
      return inj.get(key.token, notFoundValue);
    } else {
      return this._throwOrNull(key, notFoundValue);
    }
  }
/**
 * @return {?}
 */
get displayName(): string {
    const /** @type {?} */ providers =
        _mapProviders(this, (b: ResolvedReflectiveProvider) => ' "' + b.key.displayName + '" ')
            .join(', ');
    return `ReflectiveInjector(providers: [${providers}])`;
  }
/**
 * @return {?}
 */
toString(): string { return this.displayName; }
}

function ReflectiveInjector__tsickle_Closure_declarations() {
/**
 * \@internal
 * @type {?}
 */
ReflectiveInjector_.prototype._constructionCounter;
/**
 * \@internal
 * @type {?}
 */
ReflectiveInjector_.prototype._providers;
/**
 * \@internal
 * @type {?}
 */
ReflectiveInjector_.prototype._parent;
/** @type {?} */
ReflectiveInjector_.prototype.keyIds;
/** @type {?} */
ReflectiveInjector_.prototype.objs;
}


const /** @type {?} */ INJECTOR_KEY = ReflectiveKey.get(Injector);
/**
 * @param {?} injector
 * @param {?} fn
 * @return {?}
 */
function _mapProviders(injector: ReflectiveInjector_, fn: Function): any[] {
  const /** @type {?} */ res: any[] = new Array(injector._providers.length);
  for (let /** @type {?} */ i = 0; i < injector._providers.length; ++i) {
    res[i] = fn(injector.getProviderAtIndex(i));
  }
  return res;
}
